
/**
 * Search widget for menus.
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.3.3
 * @copyright  Copyright (C) 2021-2024, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2021-12-30 (1.0.0) (jhoward)
 *    - Refactored from textual-menu.vue to be used with visual-menu.vue as well.
 *
 *  2022-01-11 (1.1.0) (jhoward)
 *    - Support fixed positioning.
 *
 *  2022-03-07 (1.1.1) (jhoward)
 *    - Do not show menu search results if search input is blank.
 *
 *  2022-05-11 (1.2.0) (jhoward)
 *    - Update minimized layout.
 *
 *  2022-05-24 (1.2.1) (jhoward)
 *    - <search-menu> now uses .visual_menu--glass_effect for stylization.
 *    - <search-menu> refactored handle click logic to handleSearchHandleClick() method.
 *
 *  2023-04-21 (1.3.0) (jhoward)
 *    - <search-menu> now uses TypeScript.
 *
 *  2023-08-03 (1.3.1) (jhoward)
 *    - Erase search term after submitting search or closing the search widget in <search-menu>.
 *
 *  2023-04-21 (1.3.2) (jhoward)
 *    - Add `open` and `close` event listeners on <search-menu> container element.
 *    - Coding style improvements in <search-menu>.
 *
 *  2023-08-26 (1.3.3) (jhoward)
 *    - Use store for selected items in <search-menu>.
 */

import {ComponentPublicInstance, defineComponent, PropType} from "vue";
import TextInput from '../material-form/text-input.vue';
import {Menu} from "./defs";
import {TextInputComponent} from "../material-form/defs";

export interface SearchMenuComponent extends ComponentPublicInstance {
  reset: () => void;
}

interface AutocompleteResult {
  menu: string;
  menu_id: number;
  mi_highlight: number;
  mi_item_group_name: string;
  mi_item_subgroup_name: string;
  mi_price: string;
  mi_id: number;
  mi_title: string;
  mi_subtitle: string;
  mi_description: string;
  ms_id: number;
  mt_title: string;
  section: string;
  tags: string[];
  _formatted: {
    menu: string;
    menu_id: number;
    mi_id: number;
    mi_item_group_name: string;
    mi_item_subgroup_name: string;
    mi_highlight: number;
    mi_price: string;
    mi_title: string;
    mi_subtitle: string;
    mi_description: string;
    ms_id: number;
    mt_title: string;
    section: string;
    tags: string[];
  };
}

interface AutocompleteResponse {
  exhaustiveNbHits: boolean;
  hits?: AutocompleteResult[];
  limit: number;
  nbHits: number;
  offset: number;
  processingTimeMs: number;
  query: string;
}

export default defineComponent({
  name: "search-menu",
  components: {
    TextInput,
  },
  props: {
    searchIndex: {
      type: String,
      default: ''
    },
    searchKey: {
      type: String,
      default: ''
    },
    searchHost: {
      type: String,
      default: ''
    },
    menu: {
      type: Object as PropType<Menu | null>,
      default: null
    },
    minimizeWidget: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchTerm: '',
      autocompleteResults: [] as AutocompleteResult[],
      showResults: false,
      hideWidget: this.minimizeWidget,
      observer: null as IntersectionObserver | null,
      isStickyStuck: false,
      isFixedPosition: false,
      windowScrollY: 0,
      windowHeight: window.innerHeight,
      autocompleteMaxHeightRender: 0,

      abortController: null as AbortController | null,
      abortSignal: null as AbortSignal | null,
    };
  },
  mounted() {
    this.isFixedPosition = (window.getComputedStyle(this.$el).position === 'fixed');

    this.setupIntersectionObserver();

    document.addEventListener('click', () => {
      this.showResults = false;
      if (this.minimizeWidget) {
        this.hideWidget = true;
      }
    });

    window.addEventListener('scroll', () => {
      this.windowScrollY = window.scrollY;
    }, {passive: true});

    window.addEventListener('resize', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
    window.addEventListener('load', () => {
      this.windowHeight = window.innerHeight;
    }, {passive: true});
  },
  watch: {
    autocompleteResults() {
      this.autocompleteMaxHeightRender++;
    },
    hideWidget() {
      if (this.hideWidget) {
        this.reset();
      }
    }
  },
  computed: {

    autocompleteMaxHeight() {
      this.windowScrollY;
      this.autocompleteMaxHeightRender;
      let maxHeight = this.windowHeight;

      if (this.$refs.autocomplete) {
        maxHeight -= (this.$refs.autocomplete as HTMLLIElement).getBoundingClientRect().top;
      }

      if (this.$parent?.$el) {
        const sectionNavNode = this.$parent.$el.querySelector('.body_container--section_nav');
        if (sectionNavNode) {
          maxHeight -= sectionNavNode.clientHeight;
        }
      }

      return maxHeight + 'px';
    },

    containerClasses() {
      return {
        'search--container-minimize': this.minimizeWidget,
        'search--container-open': this.minimizeWidget && !this.hideWidget,
        'sticky-stuck': this.isStickyStuck || this.isFixedPosition
      };
    },

    searchHandleClasses() {
      return {
        'material-icons': !this.minimizeWidget || this.hideWidget
      };
    }

  },
  methods: {

    reset() {
      if (this.$refs?.input) {
        (this.$refs.input as TextInputComponent).currentValue = '';
      }
      this.autocompleteResults = [];
      this.showResults = false;
    },

    checkStickyStuck(entries: IntersectionObserverEntry[]) {
      this.isStickyStuck = (entries[0].intersectionRatio < 1);
    },

    setupIntersectionObserver() {
      if (window?.IntersectionObserver && !this.isFixedPosition) {
        //only need to set up the observer once
        if (!this.observer) {
          this.observer = new IntersectionObserver(this.checkStickyStuck, {
            threshold: [1]
          });
          if (this.$el.previousElementSibling) {
            this.observer.observe(this.$el.previousElementSibling);
          } else {
            this.observer.observe(this.$el);
          }
        }
      }
    },

    focusHandler() {
      if (this.searchTerm !== '') {
        this.showResults = true;
      }
    },

    submitAutocomplete() {
      if (this.searchTerm === '') {
        this.showResults = false;
      } else {

        if (this.abortSignal && this.abortController) {
          this.abortController.abort();
        }

        if (window?.AbortController) {
          this.abortController = new AbortController();
          this.abortSignal = this.abortController.signal;
        }

        fetch(this.searchHost + '/indexes/' + this.searchIndex + '/search', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            'X-Meili-API-Key': this.searchKey
          },
          redirect: 'follow',
          body: JSON.stringify({
            q: this.searchTerm,
            filter: 'menu_id=' + this.menu?.menu_id || 0,
            cropLength: 50,
            attributesToCrop: ['mi_title', 'mi_subtitle', 'mi_description'],
            attributesToHighlight: ['mi_title', 'mi_subtitle', 'mi_description'],
          }),
          signal: this.abortSignal
        })
          .then(response => {
            return response.json();
          })
          .then((json: AutocompleteResponse) => {
            if (json?.hits) {
              this.autocompleteResults = json.hits;
              this.showResults = true;
            } else {
              this.autocompleteResults = [];
              this.showResults = false;
            }
          })
          .catch(e => {
            console.log(e);
          })
          .finally(() => {
            this.abortController = null;
            this.abortSignal = null;
          })
      }
    },

    submitSearch() {
      if (!this.searchTerm.length) {
        this.reset();
      } else {
        //TODO: filter down to selected results?
      }
    },

    handleSearchHandleClick() {
      this.hideWidget = !this.hideWidget;

      if (!this.hideWidget) {
        (this.$refs.input as TextInputComponent | null)?.$el.querySelector('input')?.focus();
      } else {
        this.showResults = false;
      }
    },

    handleResultClick(result: AutocompleteResult) {
      this.$emit('view-menu-item', result.mi_id);
      this.showResults = false;
      this.hideWidget = true;
      this.reset();
    }

  }
})
